import { initLazyImages } from '../utils/lazy-images.js';
import { debounce } from '../utils/utils.js';
import { createDebugger } from '../utils/debug.js';
import { initLazyScript } from '../utils/theme-functions.js';
import { getSectionId } from '../utils/shopify-utils.js';

const debug = createDebugger('filter-form', false);

/**
 * Shared state manager for coordinating multiple FilterForm instances
 * Ensures only one update happens at a time while keeping all forms in sync
 */
class FilterFormStateManager {
    constructor() {
        this.instances = new Set();
        this.isUpdating = false;
        this.pendingUpdate = null;
        this.currentSearchParams = '';
    }

    /**
     * Register a FilterForm instance
     * @param {FilterForm} instance - The FilterForm instance to register
     */
    register(instance) {
        this.instances.add(instance);
        debug.log('Registered filter form instance. Total instances:', this.instances.size);
    }

    /**
     * Unregister a FilterForm instance
     * @param {FilterForm} instance - The FilterForm instance to unregister
     */
    unregister(instance) {
        this.instances.delete(instance);
        debug.log('Unregistered filter form instance. Total instances:', this.instances.size);
    }

    /**
     * Request an update from any instance
     * Only one update will be processed at a time
     * @param {FilterForm} requestingInstance - The instance requesting the update
     * @param {string} searchParams - The search parameters
     * @param {boolean} updateUrl - Whether to update the URL
     */
    async requestUpdate(requestingInstance, searchParams, updateUrl = true) {
        // If already updating, queue this request
        if (this.isUpdating) {
            this.pendingUpdate = { requestingInstance, searchParams, updateUrl };
            debug.log('Update already in progress, queuing request');
            return;
        }

        this.isUpdating = true;
        this.currentSearchParams = searchParams;

        try {
            // Find the primary instance (the one with results container or the requesting one)
            const primaryInstance = this.findPrimaryInstance(requestingInstance);

            if (primaryInstance) {
                debug.log('Using primary instance for update');
                await primaryInstance.performUpdate(searchParams, updateUrl);
            } else {
                debug.log('No primary instance found, using requesting instance');
                await requestingInstance.performUpdate(searchParams, updateUrl);
            }

            // Sync all other instances
            await this.syncAllInstances(searchParams, requestingInstance);
        } finally {
            this.isUpdating = false;

            // Process any pending update
            if (this.pendingUpdate) {
                const pending = this.pendingUpdate;
                this.pendingUpdate = null;
                this.requestUpdate(pending.requestingInstance, pending.searchParams, pending.updateUrl);
            }
        }
    }

    /**
     * Find the primary instance (one with results container)
     * @param {FilterForm} requestingInstance - The requesting instance
     * @returns {FilterForm|null} - The primary instance or null
     */
    findPrimaryInstance(requestingInstance) {
        // First, try to find an instance with a results container
        for (const instance of this.instances) {
            if (instance.results && instance !== requestingInstance) {
                return instance;
            }
        }

        // If requesting instance has results, use it
        if (requestingInstance.results) {
            return requestingInstance;
        }

        // Fallback to any instance
        return this.instances.values().next().value || null;
    }

    /**
     * Sync all instances with the current state
     * @param {string} searchParams - The search parameters
     * @param {FilterForm} excludeInstance - Instance to exclude from syncing
     */
    async syncAllInstances(searchParams, excludeInstance) {
        const syncPromises = [];

        for (const instance of this.instances) {
            if (instance !== excludeInstance) {
                syncPromises.push(instance.syncFormInputs(searchParams));
            }
        }

        await Promise.all(syncPromises);
        debug.log('Synced all instances with search params:', searchParams);
    }

    /**
     * Get the current search parameters
     * @returns {string} - Current search parameters
     */
    getCurrentSearchParams() {
        return this.currentSearchParams;
    }
}

// Global state manager instance
const stateManager = new FilterFormStateManager();

/**
 * FilterForm Web Component
 *
 * Manages filtering and sorting for collection and search pages.
 * Fetches filtered results via AJAX and updates the DOM without full page reload.
 *
 * @class FilterForm
 * @extends HTMLElement
 *
 * @fires on:filters:updated - Dispatched when filters are successfully applied
 * @fires on:filters:error - Dispatched when filter update fails
 */
class FilterForm extends HTMLElement {
    constructor() {
        super();
        // Use lazy initialization for performance
        initLazyScript(this, this.init.bind(this));
    }

    connectedCallback() {
        // Minimal connection logic - initialization handled by initLazyScript
    }

    disconnectedCallback() {
        window.removeEventListener('popstate', this.boundHandleHistoryChange);
        // Unregister from state manager
        stateManager.unregister(this);
    }

    /**
     * Initializes the component, caches elements, and sets up listeners.
     */
    init() {
        this.form = this.querySelector('form');
        if (!this.form) {
            console.error('FilterForm: No form element found within the component.');
            return;
        }

        // Register with state manager
        stateManager.register(this);

        // Detect section ID with robust fallback strategy
        this.detectSectionId();

        // Find the results container
        this.results = document.querySelector('.js-filter-results');

        this.productCount = document.getElementById('product-count');
        this.infiniteCollection = document.querySelector('infinite-collection');

        this.showIfFilterElements = document.querySelectorAll('[data-show-if-filters]');
        this.selectedCountElements = document.querySelectorAll('[data-filters-selected]');

        this.debouncedApplyFilterChange = debounce(this.applyFilterChange.bind(this), 300);
        this.boundHandleHistoryChange = this.handleHistoryChange.bind(this);

        this.addListeners();

        const initialSearchParams = window.location.search.substring(1);
        this.updateUI(initialSearchParams);
        this.updateSelectedCount();
    }

    /**
     * Detects the correct section ID for fetching filtered content.
     * Uses multiple strategies with fallbacks for robustness.
     */
    detectSectionId() {
        // Strategy 1: Check for explicit data attribute
        if (this.dataset.sectionId) {
            this.shopifySectionId = this.dataset.sectionId;
            debug.log('Using explicit section ID from data attribute:', this.shopifySectionId);
            return;
        }

        // Strategy 2: Try to get from results container's parent section
        const results = document.querySelector('.js-filter-results');
        if (results) {
            this.shopifySectionId = getSectionId(results);
            debug.log('Section ID detected from results container:', this.shopifySectionId);
        }
    }

    /**
     * Helper to pause/resume infinite scroll if present.
     * Re-queries to handle dynamic initialization.
     * @param {boolean} pause - Whether to pause (true) or resume (false)
     */
    setInfiniteScrollPause(pause) {
        const infiniteCollection = document.querySelector('infinite-collection');
        if (infiniteCollection) {
            infiniteCollection.dataset.pauseInfiniteScroll = pause.toString();
        }
    }

    /**
     * Adds event listeners to the form and window.
     */
    addListeners() {
        this.form?.addEventListener('input', this.handleFormInput.bind(this));

        this.form?.addEventListener('click', this.handleClick.bind(this));
        window.addEventListener('popstate', this.boundHandleHistoryChange);
    }

    /**
     * Handles 'click' events, specifically for clearing filter sections.
     * @param {MouseEvent} event
     */
    handleClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const clearButton = target.closest('[data-clear-filter]');

        if (clearButton && this.form) {
            event.preventDefault();
            const details = clearButton.closest('details');
            if (!details) return;

            // Clear checkboxes and radio buttons
            const inputsToClear = details.querySelectorAll('input[type="checkbox"], input[type="radio"]');
            inputsToClear.forEach((input) => {
                if (input instanceof HTMLInputElement) {
                    input.checked = false;
                }
            });

            // Reset range inputs to minimum value
            const rangesToReset = details.querySelectorAll('input[type="range"]');
            rangesToReset.forEach((range) => {
                if (range instanceof HTMLInputElement) {
                    range.value = range.min || '0';
                    range.dispatchEvent(new Event('input', { bubbles: true }));
                }
            });

            this.handleFormInput();
        }
    }

    /**
     * Handles 'input' events on the form. Updates counts and debounces filter application.
     * @param {Event} event - The input event object (optional, can be called directly).
     */
    handleFormInput() {
        this.updateSelectedCount();
        this.debouncedApplyFilterChange();
    }

    /**
     * Gathers form data, prepares search params, and triggers filter application.
     */
    applyFilterChange() {
        if (!this.form) return;

        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (value === '') continue;

            searchParams.append(key, value);
        }

        // Use state manager to coordinate updates
        stateManager.requestUpdate(this, searchParams.toString());
    }

    /**
     * Handles browser history changes (back/forward buttons).
     * @param {PopStateEvent} event - The popstate event object.
     */
    handleHistoryChange(event) {
        const searchParams = event.state?.searchParams ?? window.location.search.substring(1);

        // Use state manager to coordinate updates
        stateManager.requestUpdate(this, searchParams, false);
    }

    /**
     * Updates the form's input elements to match the provided search parameters.
     * Needed after back/forward navigation and for syncing between forms.
     * @param {string} searchParamsString - The query string.
     * @returns {Promise<void>}
     */
    async syncFormInputs(searchParamsString) {
        if (!this.form) return;

        const params = new URLSearchParams(searchParamsString);

        // Reset all checkboxes and radio buttons
        this.form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
            if (input instanceof HTMLInputElement) {
                input.checked = false;
            }
        });

        // Reset all other inputs
        this.form
            .querySelectorAll('input:not([type="checkbox"]):not([type="radio"]), select, textarea')
            .forEach((input) => {
                if (input instanceof HTMLInputElement) {
                    if (input.type === 'range') {
                        input.value = input.min || '0';
                    } else {
                        input.value = '';
                    }
                } else if (input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement) {
                    input.value = '';
                }
            });

        // Apply values from URL params
        for (const [key, value] of params.entries()) {
            const inputs = this.form.querySelectorAll(`[name="${key}"]`);
            inputs.forEach((input) => {
                if (
                    input instanceof HTMLInputElement &&
                    (input.type === 'checkbox' || input.type === 'radio')
                ) {
                    if (input.value === value) {
                        input.checked = true;
                    }
                } else if (input instanceof HTMLInputElement) {
                    input.value = value;
                    if (input.type === 'range') {
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                } else if (input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement) {
                    input.value = value;
                }
            });
        }

        this.updateSelectedCount();
        this.updateUI(searchParamsString);

        debug.log('Synced form inputs with params:', searchParamsString);
    }

    /**
     * Fetches filtered results and updates the DOM.
     * This method is called by the state manager to perform the actual update.
     * @param {string} searchParams - The filter/sort query string.
     * @param {boolean} [updateUrl=true] - Whether to update the browser URL/history.
     */
    async performUpdate(searchParams, updateUrl = true) {
        if (!this.results) {
            debug.log('FilterForm: No results container found, skipping update');
            return;
        }

        // Pause infinite scroll during filter update
        this.setInfiniteScrollPause(true);

        this.results.classList.add('is-loading');
        this.results.setAttribute('aria-busy', 'true');
        this.updateUI(searchParams);

        try {
            const fetchUrl = `${window.location.pathname}?section_id=${this.shopifySectionId}&${searchParams}`;
            debug.log('Fetching filters from:', fetchUrl);

            const response = await fetch(fetchUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Update product count
            const newProductCount = doc.getElementById('product-count');
            if (this.productCount && newProductCount) {
                this.productCount.innerHTML = newProductCount.innerHTML;
            }

            // Update results
            const newResults = doc.querySelector('.js-filter-results');
            if (newResults) {
                this.results.innerHTML = newResults.innerHTML;
            } else {
                console.warn('FilterForm: No results found in fetched HTML.');
                this.results.innerHTML = '<p>No results found.</p>';
            }

            // Reinitialize lazy images for new content
            initLazyImages();

            // Reload infinite collection if present (re-query for dynamic initialization)
            const infiniteCollection = document.querySelector('infinite-collection');
            if (infiniteCollection?.reload) {
                infiniteCollection.reload();
            }

            // Update browser URL
            if (updateUrl) {
                FilterForm.updateURL(searchParams);
            }

            // Resume infinite scroll
            this.setInfiniteScrollPause(false);

            // Dispatch custom event for external listeners
            this.dispatchEvent(new CustomEvent('on:filters:updated', { bubbles: true }));
        } catch (error) {
            console.error('FilterForm: Error fetching or applying filters:', error);
            this.results.innerHTML = '<p>Sorry, there was an error loading results.</p>';

            // Dispatch error event for external monitoring
            this.dispatchEvent(
                new CustomEvent('on:filters:error', {
                    bubbles: true,
                    detail: { error, searchParams }
                })
            );
        } finally {
            this.results.classList.remove('is-loading');
            this.results.setAttribute('aria-busy', 'false');
        }
    }

    /**
     * Updates the browser's URL and history state.
     * @param {string} searchParams - The query string.
     */
    static updateURL(searchParams) {
        const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
        window.history.pushState({ searchParams }, '', url);
    }

    /**
     * Updates UI elements based on whether any filters are active.
     * Uses cached element list.
     * @param {string} searchParams - The query string.
     */
    updateUI(searchParams) {
        const hasFilters = searchParams.length > 0;
        debug.log('Filters active:', hasFilters, '| Params length:', searchParams.length);
        this.showIfFilterElements.forEach((el) => {
            el.classList.toggle('hidden', !hasFilters);
        });
    }

    /**
     * Updates the display of how many filters are selected within each details/filter group.
     * Uses cached element list.
     */
    updateSelectedCount() {
        this.selectedCountElements.forEach((countEl) => {
            const detailsEl = countEl.closest('details');
            if (!detailsEl) return;

            const checkedInputs = detailsEl.querySelectorAll(
                'input[type="checkbox"]:checked, input[type="radio"]:checked'
            );
            const count = checkedInputs.length;

            countEl.textContent = count.toString();

            const summary = detailsEl.querySelector('summary');
            if (summary) {
                summary.classList.toggle('has-active-filters', count > 0);
            }
        });
    }
}

customElements.define('filter-form', FilterForm);
